// @ts-nocheck
import Editor from './../../../../../components/Editor/Editor';
import { Clock, FileText, MapPin, User, Phone } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { formatTimeLocale } from '../../../../../utils/dateUtility';
import { openMaps } from '../../../helpers/contact';
import { useTimeFormat } from '../../../../../hooks/useTimeFormat';
import * as PropTypes from 'prop-types';

/**
 * A service card component for transfers.
 *
 * @param {Object} servicefields - the fields of the service
 * @param {string} tripdetaileddayserviceid - the id of the tripdetaileddayservice
 * @param {boolean} isPreview - whether the service card is in preview mode
 * @param {boolean} isDesktop - whether the device is desktop
 * @returns {JSX.Element} - The JSX element representing a transfer service card.
 */
const TransferCard = ({
  servicefields,
  tripdetaileddayserviceid,
  isPreview,
  isDesktop
}) => {
  const { t } = useTranslation(['services']);
  const { timeFormat } = useTimeFormat();

  //Preview Content
  if (isPreview) {
    return (
      <div>
        <div className='activity-card__header'>
          <h3 className='activity-card__title'>{servicefields?.TITLE || ''}</h3>
        </div>
        {servicefields?.PICKUPTIME && (
          <div className='activity-card__time'>
            <Clock size={14} weight='regular' />
            <span>
              {formatTimeLocale(servicefields?.PICKUPTIME, timeFormat)}
            </span>
          </div>
        )}

        <div className='activity-card__description-container'>
          {servicefields?.COMPANYNAME && (
            <div>
              <p className='activity-card__description'>
                {servicefields.COMPANYNAME || ''}
              </p>
            </div>
          )}

          {servicefields?.CONFIRMATIONINFORMATION && (
            <div className='activity-card__confirmation'>
              {t('services:confirmationLabel', 'Confirmation')}:{' '}
              {servicefields.CONFIRMATIONINFORMATION || ''}
            </div>
          )}
        </div>
        {isDesktop && (
          <>
            <div className='activity-card__desktop-preview'>
              {servicefields?.PICKUPTIME || servicefields?.PICKUPADDRESS ? (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:pickup', 'Pickup')}
                  </p>

                  {servicefields?.PICKUPTIME && (
                    <div className='activity-card__modal-field__item'>
                      <Clock size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {formatTimeLocale(servicefields.PICKUPTIME, timeFormat)}
                      </p>
                    </div>
                  )}

                  {servicefields?.PICKUPADDRESS && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(servicefields.PICKUPADDRESS);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>
                          {servicefields?.PICKUPADDRESS || ''}
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              ) : null}

              {servicefields?.DROPOFFTIME || servicefields?.DROPOFFADDRESS ? (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:dropoff', 'Dropoff')}
                  </p>

                  {servicefields?.DROPOFFTIME && (
                    <div className='activity-card__modal-field__item'>
                      <Clock size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {formatTimeLocale(
                          servicefields.DROPOFFTIME,
                          timeFormat
                        )}
                      </p>
                    </div>
                  )}

                  {servicefields?.DROPOFFADDRESS && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(servicefields.DROPOFFADDRESS);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>
                          {servicefields?.DROPOFFADDRESS || ''}
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              ) : null}

              {servicefields?.DRIVERNAME && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:driver', 'Driver')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <User size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      {servicefields.DRIVERNAME || ''}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {servicefields?.PHONENUMBER && (
              <div className='activity-card__modal-field'>
                <p className='activity-card__modal-field__title'>
                  {t('services:contact', 'Contact')}
                </p>
                <div className='activity-card__modal-field__item'>
                  <Phone size={16} color='#667387' />
                  <a
                    className='activity-card__modal-field__value activity-card__modal-field__value--phone selectable-link'
                    href={`tel:${servicefields.PHONENUMBER || ''}`}
                    title={t('services:callPhone', 'Call Phone')}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span className='selectable-link'>
                      {servicefields.PHONENUMBER || ''}
                    </span>
                  </a>
                </div>
              </div>
            )}

            {servicefields?.DESCRIPTION && (
              <div className='activity-card__modal-rich-text'>
                <Editor
                  id={`service-editor-${tripdetaileddayserviceid}-description`}
                  content={servicefields?.DESCRIPTION}
                  editable={false}
                  isPreviewing={true}
                />
              </div>
            )}
            {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
              <div className='activity-card__modal-field'>
                <p className='activity-card__modal-field__title'>
                  {t('services:attachedDocument', 'Attached Document')}
                </p>
                <div>
                  {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                    <a
                      key={idx}
                      href={attachment?.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='activity-card__attachment-link'
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <FileText size={20} />
                      <span>{attachment?.originalName}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  //Modal Content
  return (
    <div id={`service-card-transfer-${tripdetaileddayserviceid}`}>
      <div>
        {servicefields?.CONFIRMATIONINFORMATION && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:confirmationLabel', 'Confirmation')}
            </p>
            <div className='activity-card__modal__confirmation'>
              {servicefields.CONFIRMATIONINFORMATION || ''}
            </div>
          </div>
        )}

        {servicefields?.COMPANYNAME && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:provider', 'Provider')}
            </p>
            <p className='activity-card__modal-field__value'>
              {servicefields.COMPANYNAME || ''}
            </p>
          </div>
        )}

        {servicefields?.PICKUPTIME || servicefields?.PICKUPADDRESS ? (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:pickup', 'Pickup')}
            </p>

            {servicefields?.PICKUPTIME && (
              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {formatTimeLocale(servicefields.PICKUPTIME, timeFormat)}
                </p>
              </div>
            )}

            {servicefields?.PICKUPADDRESS && (
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(servicefields.PICKUPADDRESS);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>
                    {servicefields?.PICKUPADDRESS || ''}
                  </span>
                </a>
              </div>
            )}
          </div>
        ) : null}

        {servicefields?.DROPOFFTIME || servicefields?.DROPOFFADDRESS ? (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:dropoff', 'Dropoff')}
            </p>

            {servicefields?.DROPOFFTIME && (
              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {formatTimeLocale(servicefields.DROPOFFTIME, timeFormat)}
                </p>
              </div>
            )}

            {servicefields?.DROPOFFADDRESS && (
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(servicefields.DROPOFFADDRESS);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>
                    {servicefields?.DROPOFFADDRESS || ''}
                  </span>
                </a>
              </div>
            )}
          </div>
        ) : null}

        {servicefields?.DRIVERNAME && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:driver', 'Driver')}
            </p>
            <div className='activity-card__modal-field__item'>
              <User size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                {servicefields.DRIVERNAME || ''}
              </p>
            </div>
          </div>
        )}

        {servicefields?.PHONENUMBER && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:contact', 'Contact')}
            </p>
            <div className='activity-card__modal-field__item'>
              <Phone size={16} color='#667387' />
              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                href={`tel:${servicefields.PHONENUMBER || ''}`}
                title={t('services:callPhone', 'Call Phone')}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <span className='selectable-link'>
                  {servicefields.PHONENUMBER || ''}
                </span>
              </a>
            </div>
          </div>
        )}

        {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:attachedDocument', 'Attached Document')}
            </p>
            <div>
              {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                <a
                  key={idx}
                  href={attachment?.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='activity-card__attachment-link'
                >
                  <FileText size={20} />
                  <span>{attachment?.originalName}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {servicefields?.DESCRIPTION && (
          <div className='activity-card__modal-rich-text'>
            <Editor
              id={`service-editor-${tripdetaileddayserviceid}-description`}
              content={servicefields?.DESCRIPTION}
              editable={false}
              isPreviewing={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

TransferCard.propTypes = {
  servicefields: PropTypes.object,
  servicetype: PropTypes.object,
  tripdetaileddayserviceid: PropTypes.number,
  isPreview: PropTypes.bool,
  isDesktop: PropTypes.bool
};

export default TransferCard;
